import { useCallback, useEffect } from 'react';
import { useHref, useLocation, useSearchParams } from 'react-router-dom';
import type { ChatDialogResponse } from '@/core/generated/api';
import { useAuth } from '@/entities/Auth/store';
import { useChatDialogWorkspace, useChatDialogWorkspaceApi } from '@/entities/ChatDialog/model/workspace';

/** Открывает переписку по ссылке и обновляет её адрес */
export const useChatDialogRoute = (enabled: boolean) => {
    const store = useChatDialogWorkspaceApi();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const chooseDialog = useChatDialogWorkspace((state) => state.selectDialog);
    const clearSelection = useChatDialogWorkspace((state) => state.clearSelection);
    const dialogId = searchParams.get('dialogId')?.trim() || null;
    const dialogUrl = new URL(useHref(location), window.location.origin).href;

    useEffect(() => {
        if (!enabled) return;

        if (dialogId) {
            const state = store.getState();
            const dialog = state.dialogs.find((item) => item.id === dialogId);
            chooseDialog(dialog ?? { id: dialogId });
        } else {
            clearSelection();
        }
    }, [enabled, dialogId, sessionRevision, chooseDialog, clearSelection, store]);

    /** Выбирает диалог и сохраняет его адрес */
    const selectDialog = useCallback((dialog: ChatDialogResponse): void => {
        const id = dialog.id;

        if (!id) return;

        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            next.set('dialogId', id);

            return next;
        });

        chooseDialog(dialog);
    }, [chooseDialog, setSearchParams]);

    /** Возвращает к списку диалогов */
    const closeDialog = useCallback((): void => {
        if (dialogId) {
            setSearchParams((current) => {
                const next = new URLSearchParams(current);
                next.delete('dialogId');

                return next;
            });
        }

        clearSelection();
    }, [dialogId, setSearchParams, clearSelection]);

    return {
        selectDialog,
        closeDialog,
        dialogUrl,
    };
};
